import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, MenuItem, Select, FormControl, InputLabel, TablePagination,
  CircularProgress, Alert, Typography,
} from '@mui/material';
import { adminApi } from '../services/hrmsApi';

const ACTIONS = [
  'USER_CREATE', 'ROLE_UPDATE', 'LOGIN', 'LOGOUT',
  'USER_DEACTIVATE', 'USER_REACTIVATE', 'USER_PROFILE_UPDATE',
  'PASSWORD_RESET_ADMIN', 'PASSWORD_CHANGE_SELF', 'ACCOUNT_UNLOCK', 'ACCOUNT_LOCKED',
];

const ACTION_COLORS = {
  USER_CREATE: '#2E7D32', ROLE_UPDATE: '#1565C0', LOGIN: '#37474F', LOGOUT: '#757575',
  USER_DEACTIVATE: '#C62828', USER_REACTIVATE: '#2E7D32', USER_PROFILE_UPDATE: '#6A1B9A',
  PASSWORD_RESET_ADMIN: '#E65100', PASSWORD_CHANGE_SELF: '#E65100', ACCOUNT_UNLOCK: '#00695C',
  ACCOUNT_LOCKED: '#C62828',
};

export default function AuditLogPanel({ users, initialUserId }) {
  const [userId, setUserId] = useState(initialUserId || '');
  const [action, setAction] = useState('');
  const [page,   setPage]   = useState(0);
  const [size]              = useState(10);

  const [entries,       setEntries]       = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);

  useEffect(() => { setUserId(initialUserId || ''); setPage(0); }, [initialUserId]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    adminApi.getAuditLog({ userId: userId || undefined, action: action || undefined, page, size })
      .then((res) => {
        setEntries(res?.content || []);
        setTotalElements(res?.totalElements ?? (res?.content || []).length);
      })
      .catch(() => setError('Failed to load audit log.'))
      .finally(() => setLoading(false));
  }, [userId, action, page, size]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>User</InputLabel>
            <Select label="User" value={userId} onChange={(e) => { setUserId(e.target.value); setPage(0); }}>
              <MenuItem value="">All users</MenuItem>
              {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.fullName}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Action</InputLabel>
            <Select label="Action" value={action} onChange={(e) => { setAction(e.target.value); setPage(0); }}>
              <MenuItem value="">All actions</MenuItem>
              {ACTIONS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No audit entries found.
          </Typography>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>TIME</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>USER</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>ACTION</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>RESOURCE</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>DETAIL</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>IP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {e.createdAt ? new Date(e.createdAt).toLocaleString('en-MY') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2">{e.actorName}</Typography></TableCell>
                    <TableCell>
                      <Chip label={e.action} size="small" sx={{
                        fontWeight: 700, fontSize: '0.68rem',
                        bgcolor: (ACTION_COLORS[e.action] || '#546E7A') + '18',
                        color: ACTION_COLORS[e.action] || '#546E7A',
                      }} />
                    </TableCell>
                    <TableCell><Typography variant="caption">{e.resource}</Typography></TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{e.detail}</Typography></TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{e.ipAddress}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalElements}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={size}
              rowsPerPageOptions={[size]}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
